import React from "react";
import {
  FormControl,
  InputAdornment,
  OutlinedInput,
  Typography,
  useMediaQuery,
} from "@mui/material";
import "./PaymentCalculator.css";
import Button from "@mui/material/Button";
import { useState, useEffect } from "react";

const PaymentCalculator = () => {
  // State to hold input values

  const [balance, setBalance] = useState(100000); // Credit card balance
  const [payment, setPayment] = useState(12000); // Monthly payment
  const [APR, setAPR] = useState(10.1); // APR in percentage
  const [results, setResults] = useState(null);
  const [warning, setWarning] = useState(""); // Warning message state

  // Function to calculate monthly payment details
  const calculatePaymentDetails = () => {
    const monthlyRate = APR / 12 / 100; // Convert APR to monthly rate

    if (payment <= balance * monthlyRate) {
      setResults({
        months: 0,
        totalInterest: 0,
        totalPaid: 0,
      });
      return;
    }

    // Calculate number of months using the amortization formula
    const months = Math.ceil(
      Math.log(payment / (payment - balance * monthlyRate)) /
        Math.log(1 + monthlyRate)
    );

    // Calculate total interest paid
    const totalPaid = months * payment;
    const totalInterest = totalPaid - balance;

    // Set results
    setResults({
      months: isNaN(months) ? 0 : months,
      totalInterest: isNaN(totalInterest) ? 0 : totalInterest,
      totalPaid: isNaN(totalPaid) ? 0 : totalPaid,
    });
  };

  useEffect(() => {
    if (results) {
      calculatePaymentDetails();
    }
  }, [balance, payment, APR]);

  // UseEffect to check for low payment warning
  useEffect(() => {
    const monthlyRate = APR / 12 / 100; // Monthly interest rate
    if (payment > 0 && payment <= balance * monthlyRate) {
      setWarning(
        "Your monthly payment is too low to cover the interest. Please increase the payment amount."
      );
    } else {
      setWarning(""); // Clear the warning if payment is sufficient
    }
  }, [balance, payment, APR]);

  const formatCurrency = (value) => {
    return value.toLocaleString("en-IN");
  };

  const handleAmountChange = (e, setter) => {
    const value = e.target.value.replace(/,/g, ""); // Remove commas
    if (/^\d*\.?\d*$/.test(value)) {
      const numericValue = Number(value); // Convert to number
      if (numericValue <= 5000000) {
        // Restrict to maximum 50,00,000
        setter(numericValue);
      }
    }
  };

  // Function to format numbers in Indian currency format
  const formatToINR = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // useMediaquries
  const isMediumScreen = useMediaQuery(" (max-width:768px) ");
  const isLargeScreen = useMediaQuery(" (max-width:1200px) ");

  return (
    <>
      <div className="payment_calculator">
        <div className="monthly_payment_calculator">
          <div className="top-line"></div>
          <div className="calculator_details p-4">
            <p className="payment_calculator_title  fs-1 fw-bold calulator_font">
              Monthly payment calculator
            </p>
            <p
              className="title_description calculator_font size "
              style={{ fontWeight: 500 }}
            >
              This calculator shows you exactly how much you would have to pay
              each month to eliminate credit card debt within a specific time
              frame.
            </p>

            <div className="line"></div>

            <div className="starting_card d-flex justify-content-between gap-3 pt-4 flex-column flex-md-row">
              <div className="  w-100">
                <label className="calculator_font size fw-bold ">
                  Current Credit Card Balance (₹){" "}
                </label>
                <FormControl
                  fullWidth
                  sx={{
                    backgroundColor: "#ffffff",

                    border: "1.5px solid #b783cc",
                    borderRadius: "5px",
                    paddingTop: "0px",
                    fontFamily: "'Archivo',sans-serif",
                  }}
                >
                  <OutlinedInput
                    id="outlined-adornment-amount"
                    fullWidth
                    size="small"
                    startAdornment={
                      <InputAdornment position="start">
                        <span className="archivo">&#8377;</span>
                      </InputAdornment>
                    }
                    value={formatCurrency(balance || "")} // Display value with commas
                    onChange={(e) => handleAmountChange(e, setBalance)}
                  />
                </FormControl>
              </div>
              <div className="  w-100 fw-bold">
                <label className="calculator_font size">
                  Monthly Payment Amount (₹){" "}
                </label>

                <FormControl
                  fullWidth
                  sx={{
                    backgroundColor: "#ffffff",

                    border: "1.5px solid #b783cc",
                    borderRadius: "5px",
                    paddingTop: "0px",
                    fontFamily: "'Archivo',sans-serif",
                  }}
                >
                  <OutlinedInput
                    id="outlined-adornment-amount"
                    fullWidth
                    size="small"
                    startAdornment={
                      <InputAdornment position="start">
                        <span className="archivo">&#8377;</span>
                      </InputAdornment>
                    }
                    // value={}
                    // onChange={(e) => setPayment(Number(e.target.value))}

                    value={formatCurrency(payment || "")} // Display value with commas
                    onChange={(e) => handleAmountChange(e, setPayment)}
                  />
                </FormControl>
                {warning && (
                  <Typography
                    sx={{
                      color: "#820082",
                      fontSize: "14px",
                      marginTop: "5px",
                    }}
                  >
                    {warning}
                  </Typography>
                )}
              </div>
            </div>

            <div
              style={{
                width: isMediumScreen ? "100%" : isLargeScreen ? "50%" : "49%",
              }}
              className="pt-3"
            >
              <label className="calculator_font size fw-bold">
                Interest Rate (APR %){" "}
              </label>
              <FormControl
                fullWidth
                sx={{
                  backgroundColor: "#ffffff",
                  border: "1.5px solid #b783cc",
                  borderRadius: "5px",
                  paddingTop: "0px",
                  fontFamily: "'Archivo',sans-serif",
                }}
              >
                <OutlinedInput
                  id="outlined-adornment-amount"
                  fullWidth
                  size="small"
                  endAdornment={
                    <InputAdornment position="end">
                      <span className="archivo">%</span>
                    </InputAdornment>
                  }
                  value={APR || ""}
                  onChange={(e) => setAPR(Number(e.target.value))}
                />
              </FormControl>
            </div>
            <div className="calculate_button pt-5">
              <Button
                sx={{
                  fontFamily: "'Archivo'sans-serif",
                  textTransform: "none",
                  fontSize: "16px",
                }}
                onClick={calculatePaymentDetails}
                variant="contained"
                color="secondary"
              >
                Calculate
              </Button>
            </div>
            {/* Results */}
            {results !== null && (
              <div
                className="d-flex flex-column flex-md-row justify-content-between gap-4 w-100"
                style={{ marginTop: "20px" }}
              >
                <div className="months">
                  <Typography
                    sx={{
                      fontFamily: "'Archivo', sans-serif",
                      fontSize: "13px",
                      lineHeight: "24px",
                      letterSpacing: "normal",
                      textAlign: "start",
                      fontWeight: 700,
                    }}
                  >
                    Number of Months to Pay Off Balance:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Archivo', sans-serif",
                      fontSize: "24px",
                      lineHeight: "24px",
                      letterSpacing: "normal",
                      textAlign: "start",
                      fontWeight: 700,
                      color: "#005FB9",
                    }}
                  >
                    {" "}
                    {results.months} months
                  </Typography>
                </div>
                <div className="months">
                  <Typography
                    sx={{
                      fontFamily: "'Archivo', sans-serif",
                      fontSize: "13px",
                      lineHeight: "24px",
                      letterSpacing: "normal",
                      textAlign: "start",
                      fontWeight: 700,
                    }}
                  >
                    Total Interest Paid:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Archivo', sans-serif",
                      fontSize: "24px",
                      lineHeight: "24px",
                      letterSpacing: "normal",
                      textAlign: "start",
                      fontWeight: 700,
                      color: "#005FB9",
                    }}
                    className=" text-break"
                  >
                    {" "}
                    {formatToINR(results.totalInterest)}{" "}
                  </Typography>
                </div>
                <div className="months">
                  <Typography
                    sx={{
                      fontFamily: "'Archivo', sans-serif",
                      fontSize: "13px",
                      lineHeight: "24px",
                      letterSpacing: "normal",
                      textAlign: "start",
                      fontWeight: 700,
                    }}
                  >
                    Total Amount to Be Paid:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Archivo', sans-serif",
                      fontSize: "24px",
                      lineHeight: "24px",
                      letterSpacing: "normal",
                      textAlign: "start",
                      fontWeight: 700,
                      color: "#005FB9",
                    }}
                    className=" text-break"
                  >
                    {" "}
                    {formatToINR(results.totalPaid)}{" "}
                  </Typography>
                </div>
              </div>
            )}

            <p className=" italic_text fst-italic calulator_font pt-4">
              All calculations are estimates. Finance charges will vary
              according to your credit card's terms. Your card may require a
              minimum monthly payment higher than the number shown by this
              calculator.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentCalculator;
